import { useCallback, useMemo } from 'react';

import { createStateSelectorHook } from 'src/lib/sweetState';
import {
  PerformanceStore,
  useCurrentWorkspaceId,
  useCustomerSalesforceCrmIntegrationEnabled,
  usePerformanceActions,
} from 'src/store';
import { useExternalChannelInfoPopup } from 'src/views/ExternalChannelInfoPopup';

import { useChannelAccounts } from './useChannelAccounts';
import { useConnectToChannel } from './useConnectToChannel';
import { useErrorHandler } from './useErrorHandler';

const usePerformanceState = createStateSelectorHook(PerformanceStore, [
  'externalConnectionSearchResults',
  'externalConnectionSearchLoading',
]);

const AUTH_ERROR = {
  accountNotAuthorized: 'account_not_authorized',
  accessExpired: 'access_expired',
};

export const useExternalChannelPromptMenuProps = () => {
  const performanceActions = usePerformanceActions();
  const openExternalChannelInfoPopup = useExternalChannelInfoPopup();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const { connectToChannel } = useConnectToChannel();
  const { connectedAccounts } = useChannelAccounts();
  const handleError = useErrorHandler();
  const shouldIncludeSalesforceCrm = useCustomerSalesforceCrmIntegrationEnabled();

  const { externalConnectionSearchResults, externalConnectionSearchLoading } =
    usePerformanceState();

  const handleExternalConnectionSearch = useCallback(
    async ({ channel, searchTerm, filter }) => {
      try {
        await performanceActions.externalConnectionObjectSearch({
          channel,
          searchTerm,
          filter,
          workspaceId: currentWorkspaceId,
        });
      } catch (error) {
        if (Object.values(AUTH_ERROR).includes(error.code)) {
          await performanceActions.disconnectFromChannel(channel);
          await performanceActions.queryUserIntegrationAccounts();
          connectToChannel({ channel });
          return;
        }
        handleError(error);
      }
    },
    [performanceActions, currentWorkspaceId, connectToChannel, handleError]
  );

  const handleUpdateExternalConnectionSearchLoading = useCallback(
    (value) => {
      performanceActions.setExternalConnectionSearchLoading(value);
    },
    [performanceActions]
  );

  const handleExternalConnectionSearchResultClick = useCallback(
    ({ name, items }) => {
      return openExternalChannelInfoPopup({ name, items });
    },
    [openExternalChannelInfoPopup]
  );

  const handleConnectExternalChannel = useCallback(
    ({ channel, callback }) => {
      if (connectedAccounts[channel]?.length > 0) {
        callback();
      } else {
        connectToChannel({ channel, onConnectSuccess: callback });
      }
    },
    [connectedAccounts, connectToChannel]
  );

  return useMemo(() => {
    if (!shouldIncludeSalesforceCrm) {
      return null;
    }
    return {
      onSearch: handleExternalConnectionSearch,
      onUpdateSearchLoading: handleUpdateExternalConnectionSearchLoading,
      onResultClick: handleExternalConnectionSearchResultClick,
      searchResults: externalConnectionSearchResults,
      searchLoading: externalConnectionSearchLoading,
      onConnectExternalChannel: handleConnectExternalChannel,
    };
  }, [
    handleExternalConnectionSearch,
    handleUpdateExternalConnectionSearchLoading,
    handleExternalConnectionSearchResultClick,
    externalConnectionSearchResults,
    externalConnectionSearchLoading,
    handleConnectExternalChannel,
    shouldIncludeSalesforceCrm,
  ]);
};
